import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Button, Grid, Header, Icon, Message } from 'semantic-ui-react';

import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';

import HelpTooltip from 'components/common/HelpTooltip';
import useDebounce from 'hooks/useDebounce';

import { StyleRuleInheritance, RawEditField, SpecialEditField } from './components';
import useStyleRule from '../../hooks/useStyleRule';
import { formatStyleRuleName } from '../../services/format';
import { StyleRuleConfigurationPropType } from '../../types';

import './StyleRuleSettingsItem.scss';

const propTypes = {
  styleRuleConfiguration: StyleRuleConfigurationPropType.isRequired,
  onReset: PropTypes.func,
  onChange: PropTypes.func,
  highlighted: PropTypes.bool,
  getStyleRuleInheritance: PropTypes.func,
};

const defaultProps = {
  highlighted: false,
  onReset: () => null,
  onChange: () => null,
  getStyleRuleInheritance: () => null,
};

export default function StyleRuleSettingsItem({
  styleRuleConfiguration,
  onReset: onResetProp,
  onChange: onChangeProp,
  highlighted,
  getStyleRuleInheritance,
}) {
  const { name, description = '', defaultValue = '', custom } = styleRuleConfiguration || {};
  const { styleRule, setValue, deleteRule } = useStyleRule(name);

  const { value = '', _touchedFields: touchedFields } = styleRule || {};

  const [localValue, setLocalValue] = useState(value);
  const debouncedLocalValue = useDebounce(localValue);

  const active = !_isEmpty(styleRule);

  function onChange(event, data) {
    const { value: newValue } = data;
    setLocalValue(newValue);
  }

  function onReset() {
    setLocalValue('');
    deleteRule();
    if (onResetProp) {
      onResetProp();
    }
  }

  useEffect(() => {
    if (active && _isEmpty(debouncedLocalValue)) {
      onReset();
      return;
    }

    if (!_isEmpty(debouncedLocalValue) || active) {
      const target = { value: debouncedLocalValue };

      if (debouncedLocalValue !== value) {
        setValue(debouncedLocalValue);

        if (onChangeProp) {
          onChangeProp(target, target);
        }
      }
    }
  }, [debouncedLocalValue]);

  const className = clsx('style-rule-settings-item', { highlighted });

  const defaultValueInheritance = getStyleRuleInheritance(defaultValue);
  const activeValueInheritance = getStyleRuleInheritance(value);

  const displayNeedsSave = !_isEmpty(touchedFields);

  return (
    <Grid className={className}>
      <Grid.Column className="main-column" width={13}>
        <Header>
          <span>{formatStyleRuleName(name)}</span>
          <Icon
            name="stop"
            className={clsx({ active, saved: !displayNeedsSave, 'needs-save': displayNeedsSave })}
          />
        </Header>

        <div className="description">{description}</div>

        <div className="edit">
          <div className="raw-field-container">
            <RawEditField
              styleRuleConfiguration={styleRuleConfiguration}
              value={localValue || ''}
              onChange={onChange}
            />
            <HelpTooltip
              title="Variable Information"
              content={
                <StyleRuleInheritance
                  defaultValueInheritance={defaultValueInheritance}
                  activeValueInheritance={activeValueInheritance}
                />
              }
              trigger={<Icon name="question circle" className="help-tooltip-trigger" />}
              className="style-rule-settings-item-help-tooltip"
              position="right center"
            />
          </div>

          <div className="special-field-container">
            <SpecialEditField
              styleRuleConfiguration={styleRuleConfiguration}
              value={localValue || ''}
              onChange={onChange}
            />
          </div>
        </div>
      </Grid.Column>

      <Grid.Column className="reset-column" width={3}>
        {active && (
          <Button
            content={custom ? 'Delete' : 'Reset'}
            className="action-button reset"
            onClick={onReset}
            compact
          />
        )}
        {displayNeedsSave && (
          <Message className="needs-save" warning>
            Needs save
          </Message>
        )}
      </Grid.Column>
    </Grid>
  );
}

StyleRuleSettingsItem.propTypes = propTypes;
StyleRuleSettingsItem.defaultProps = defaultProps;
